<template>
  <div class="admin-page">
    <header class="admin-top-bar">
      <div class="admin-top-inner">
        <div class="admin-top-brand">
          <span class="admin-hub-badge">{{ $t('pages.rewardAdmin.admin_4e7afeb') }}</span>
          <h1 class="admin-hub-title">{{ $t('pages.rewardAdmin.admin_system_e07b109') }}</h1>
          <p class="admin-hub-lead">
            {{ $t('pages.rewardAdmin.reward_approvals_and_aggregated_us_21b1cbc') }}
          </p>
        </div>
        <nav class="admin-hub-tabs" :aria-label="$t('pages.rewardAdmin.admin_sections_80deff5')">
          <button
            type="button"
            class="admin-hub-tab"
            :class="{ active: activeTab === 'rewards' }"
            @click="activeTab = 'rewards'"
          >
            {{ $t('pages.rewardAdmin.reward_queue_2e39291') }}
          </button>
          <button
            type="button"
            class="admin-hub-tab"
            :class="{ active: activeTab === 'analytics' }"
            @click="switchAnalytics"
          >
            {{ $t('pages.rewardAdmin.analytics_25bc962') }}
          </button>
        </nav>
      </div>
    </header>

    <!-- Rewards -->
    <div v-show="activeTab === 'rewards'" class="admin-hub-body">
      <div class="reward-toolbar">
        <p class="reward-toolbar-hint">
          {{ submissions.length }} participant row(s). Shows student accounts eligible for reward verification (reviews/comments); admin accounts are excluded. Pick the student by email/name to open their submission — not “your” logged‑in admin profile.
        </p>
        <div class="reward-toolbar-actions">
          <label class="sort-label" for="queue-sort">{{ $t('pages.rewardAdmin.order_1d75774') }}</label>
          <select id="queue-sort" v-model="queueSort" class="queue-sort">
            <option value="recent">{{ $t('pages.rewardAdmin.most_recent_activity_e73e4ff') }}</option>
            <option value="status">{{ $t('pages.rewardAdmin.status_workflow_bbc54ed') }}</option>
            <option value="name">{{ $t('pages.rewardAdmin.name_a_z_c93e4c5') }}</option>
            <option value="email">{{ $t('pages.rewardAdmin.email_a_z_05d6a57') }}</option>
          </select>
          <button type="button" class="text-btn" @click="loadQueue">{{ $t('pages.rewardAdmin.refresh_queue_50c3cf3') }}</button>
        </div>
      </div>

      <div class="reward-split">
        <aside class="reward-rail">
          <div v-if="!submissions.length" class="reward-rail-empty">
            {{ $t('pages.rewardAdmin.no_submissions_yet_users_appear_he_b87ec6d') }}
          </div>
          <button
            v-for="item in sortedSubmissions"
            :key="item.userId"
            type="button"
            class="reward-rail-card"
            :class="{ active: selectedUserId === item.userId }"
            @click="selectSubmission(item.userId)"
          >
            <div class="reward-rail-card-top">
              <span class="reward-rail-name">{{ item.name || item.email }}</span>
              <span class="status-pill" :class="statusTone(item.statusLabel)">{{ item.statusLabel }}</span>
            </div>
            <div class="reward-rail-email">{{ item.email }}</div>
            <div class="reward-rail-metrics">
              <span><em>R</em> {{ item.reviewCompleted ? '✓' : '…' }}</span>
              <span><em>{{ $t('pages.rewardAdmin.photos_c8b2e86') }}</em> {{ item.photoCount }}</span>
              <span><em>{{ $t('pages.rewardAdmin.comments_fce06e2') }}</em> {{ item.eligibleCommentCount }}</span>
            </div>
          </button>
        </aside>

        <main class="reward-main">
          <div v-if="loadingDetail" class="panel-muted">{{ $t('pages.rewardAdmin.loading_submission_b0d8c6c') }}</div>
          <div v-else-if="!selectedDetail" class="panel-muted">
            {{ $t('pages.rewardAdmin.select_a_submission_from_the_list__c02db7e') }}
          </div>
          <template v-else>
            <section class="glass-panel hero-detail">
              <div class="hero-detail-head">
                <div>
                  <h2>{{ selectedDetail.summary.name }}</h2>
                  <p class="muted">{{ selectedDetail.summary.email }}</p>
                </div>
                <span class="status-pill lg" :class="statusTone(selectedDetail.summary.statusLabel)">
                  {{ selectedDetail.summary.statusLabel }}
                </span>
              </div>
              <div class="stat-chip-row">
                <div class="stat-chip">
                  <span class="stat-chip-label">{{ $t('pages.rewardAdmin.photos_c8b2e86') }}</span>
                  <strong>{{ selectedDetail.summary.photoCount }}</strong>
                </div>
                <div class="stat-chip">
                  <span class="stat-chip-label">{{ $t('pages.rewardAdmin.eligible_comments_bcd45f2') }}</span>
                  <strong>{{ selectedDetail.summary.eligibleCommentCount }}</strong>
                </div>
                <div class="stat-chip">
                  <span class="stat-chip-label">{{ $t('pages.rewardAdmin.approved_slots_af4ed8a') }}</span>
                  <strong>{{ selectedDetail.summary.approvedCount }} / {{ selectedDetail.summary.rewardLimit }}</strong>
                </div>
              </div>
              <div v-if="selectedDetail.summary.flags && selectedDetail.summary.flags.length" class="alert-banner flags">
                <strong>{{ $t('pages.rewardAdmin.flags_5d72875') }}</strong> {{ selectedDetail.summary.flags.join(', ') }}
              </div>
              <div v-if="selectedDetail.summary.adminNote" class="alert-banner note">
                <strong>{{ $t('pages.rewardAdmin.admin_note_4193c1a') }}</strong> {{ selectedDetail.summary.adminNote }}
              </div>
            </section>

            <section class="glass-panel" v-if="selectedDetail.review">
              <h3 class="panel-title">{{ $t('pages.rewardAdmin.review_evidence_ab34d96') }}</h3>
              <p class="review-target">
                {{ selectedDetail.review.houseName }}
                <span v-if="selectedDetail.review.houseLetName"> — {{ selectedDetail.review.houseLetName }}</span>
              </p>
              <p class="review-body">{{ selectedDetail.review.content || 'No written review content.' }}</p>
              <div class="photo-chips" v-if="selectedDetail.review.photos && selectedDetail.review.photos.length">
                <div
                  v-for="(photo, i) in selectedDetail.review.photos"
                  :key="'ph-' + i"
                  class="photo-chip-slot"
                >
                  <a
                    v-if="safePhotoUrl(photo)"
                    :href="safePhotoUrl(photo)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="photo-chip"
                  >Photo {{ i + 1 }}</a>
                  <span
                    v-else
                    class="photo-chip photo-chip-bad-url"
                    :title="$t('pages.rewardAdmin.url_is_not_allowed_http_https_only_274bdb6')"
                  >Photo {{ i + 1 }}</span>
                </div>
              </div>
              <div class="review-actions">
                <button
                  type="button"
                  class="primary-btn sm"
                  @click="viewReviewPage"
                >
                  {{ $t('pages.rewardAdmin.view_on_site_b66ea87') }}
                </button>
              </div>
            </section>

            <section class="glass-panel">
              <h3 class="panel-title">{{ $t('pages.rewardAdmin.comments_fce06e2') }}</h3>
              <div v-if="!selectedDetail.comments || !selectedDetail.comments.length" class="muted soft-pad">
                {{ $t('pages.rewardAdmin.no_comments_found_for_this_user_e19abb6') }}
              </div>
              <div v-else class="comment-stack">
                <div v-for="comment in selectedDetail.comments" :key="comment.commentId" class="comment-card">
                  <div class="comment-card-title">
                    {{ comment.houseName }}<span v-if="comment.houseLetName"> — {{ comment.houseLetName }}</span>
                  </div>
                  <p>{{ comment.content }}</p>
                  <div class="flag-row">
                    <span v-if="comment.tooShort" class="flag">{{ $t('pages.rewardAdmin.too_short_b126726') }}</span>
                    <span v-if="comment.repetitive" class="flag">{{ $t('pages.rewardAdmin.repetitive_3ffacb5') }}</span>
                    <span v-if="comment.ownListing" class="flag">{{ $t('pages.rewardAdmin.own_listing_9aeb139') }}</span>
                  </div>
                </div>
              </div>
            </section>

            <section class="glass-panel reminder-panel">
              <h3 class="panel-title">{{ $t('pages.rewardAdmin.progress_reminder_email_0834ecf') }}</h3>
              <p class="reminder-lead">
                {{ $t('pages.rewardAdmin.email_this_student_about_the_hk_50_ebb8505') }}
              </p>
              <div v-if="selectedDetail.summary" class="reminder-preview">
                <span
                  class="reminder-preview-item"
                  :class="{ met: selectedDetail.summary.reviewCompleted }"
                >
                  Review {{ selectedDetail.summary.reviewCompleted ? '✓' : '…' }}
                </span>
                <span
                  class="reminder-preview-item"
                  :class="{ met: selectedDetail.summary.photoCount >= 2 }"
                >
                  Photos {{ selectedDetail.summary.photoCount }} / 2
                </span>
                <span
                  class="reminder-preview-item"
                  :class="{ met: selectedDetail.summary.eligibleCommentCount >= 1 }"
                >
                  Comments {{ selectedDetail.summary.eligibleCommentCount }} / 1
                </span>
              </div>
              <textarea
                v-model="reminderMessage"
                class="decision-input reminder-input"
                :placeholder="$t('pages.rewardAdmin.optional_personal_note_from_you_sh_a0cf2a2')"
              ></textarea>
              <div class="reminder-actions">
                <button
                  type="button"
                  class="outline-btn"
                  :disabled="reminderSubmitting || !canSendReminder"
                  @click="sendReminder('NOT_STARTED')"
                >
                  {{ $t('pages.rewardAdmin.not_started_db2c45d') }}
                </button>
                <button
                  type="button"
                  class="outline-btn"
                  :disabled="reminderSubmitting || !canSendReminder"
                  @click="sendReminder('IN_PROGRESS')"
                >
                  {{ $t('pages.rewardAdmin.incomplete_387fd1b') }}
                </button>
                <button
                  type="button"
                  class="primary-btn sm"
                  :disabled="reminderSubmitting || !canSendReminder"
                  @click="sendReminder(suggestedReminderType)"
                >
                  {{ reminderSubmitting ? 'Sending…' : 'Send suggested' }}
                </button>
              </div>
              <p v-if="!canSendReminder" class="muted soft-pad reminder-hint">
                {{ $t('pages.rewardAdmin.reminders_are_only_available_for_s_15dd308') }}
              </p>
            </section>

            <section class="glass-panel decision-panel">
              <h3 class="panel-title">{{ $t('pages.rewardAdmin.decision_7f59a1f') }}</h3>
              <textarea
                v-model="decisionNote"
                class="decision-input"
                :placeholder="$t('pages.rewardAdmin.optional_note_for_the_user_or_inte_f8e6661')"
              ></textarea>
              <div class="decision-actions">
                <button
                  type="button"
                  class="primary-btn sm"
                  :disabled="decisionSubmitting"
                  @click="submitAction('approve')"
                >
                  {{ $t('pages.rewardAdmin.approve_7b2c7f1') }}
                </button>
                <button
                  type="button"
                  class="outline-btn btn-danger"
                  :disabled="decisionSubmitting"
                  @click="submitAction('reject')"
                >
                  {{ $t('pages.rewardAdmin.reject_2b03b59') }}
                </button>
                <button
                  type="button"
                  class="outline-btn"
                  :disabled="decisionSubmitting"
                  @click="submitAction('issue')"
                >
                  {{ $t('pages.rewardAdmin.mark_issued_ddacea2') }}
                </button>
              </div>
              <p v-if="decisionSubmitting" class="muted soft-pad decision-submit-hint">{{ $t('pages.rewardAdmin.updating_3ab0d73') }}</p>
            </section>
          </template>
        </main>
      </div>
    </div>

    <!-- Analytics -->
    <div v-show="activeTab === 'analytics'" class="admin-hub-body analytics-body">
      <div class="analytics-shell">
        <header class="analytics-toolbar">
          <div class="analytics-toolbar-copy">
            <h2 class="analytics-toolbar-title">Analytics</h2>
            <p class="analytics-toolbar-sub">Usage, UGC volume, rewards, traffic, schools.</p>
          </div>
          <button
            type="button"
            class="analytics-refresh"
            :disabled="analyticsLoading"
            @click="loadAnalytics"
          >
            {{ analyticsLoading ? 'Refreshing…' : 'Refresh data' }}
          </button>
        </header>

        <div v-if="analyticsError" class="alert-banner warn analytics-banner">{{ analyticsError }}</div>

        <div v-if="summary && !hasLiveTraffic" class="analytics-callout">
          <strong>No route traffic yet</strong>
          <span
            >Charts still show; session rows stay at zero until the DB has
            <code class="analytics-code">site_page_analytics</code> (deploy migration) and users run a
            <strong>current</strong> frontend that reports page dwell.</span
          >
        </div>

        <template v-if="summary">
          <nav class="analytics-section-nav" aria-label="Analytics sections">
            <button
              v-for="item in analyticsSectionNavItems"
              :key="item.id"
              type="button"
              class="analytics-section-nav-btn"
              @click="scrollAnalyticsSection(item.id)"
            >
              {{ item.label }}
            </button>
          </nav>

          <section id="analytics-overview" class="analytics-section">
            <h3 class="analytics-section-head">
              Overview
            </h3>
            <div class="analytics-grid analytics-grid--metrics">
              <article class="analytics-metric analytics-metric--sky">
                <span class="analytics-metric-label">Total users</span>
                <strong class="analytics-metric-value">{{ summary.totalUsers }}</strong>
                <span class="analytics-metric-hint">{{ summary.adminUsers }} admins</span>
              </article>
              <article class="analytics-metric analytics-metric--sky">
                <span class="analytics-metric-label">Reviews (7d)</span>
                <strong class="analytics-metric-value">{{ summary.reviewsLast7Days }}</strong>
                <span class="analytics-metric-hint">{{ summary.totalReviews }} all-time</span>
              </article>
              <article class="analytics-metric analytics-metric--sky">
                <span class="analytics-metric-label">Comments (7d)</span>
                <strong class="analytics-metric-value">{{ summary.commentsLast7Days }}</strong>
                <span class="analytics-metric-hint">{{ summary.totalComments }} all-time</span>
              </article>
              <article class="analytics-metric analytics-metric--sky">
                <span class="analytics-metric-label">Active users (7d)</span>
                <strong class="analytics-metric-value">{{ summary.activeUserIdsLast7Days }}</strong>
                <span class="analytics-metric-hint">Distinct authors</span>
              </article>
              <article class="analytics-metric analytics-metric--reward">
                <span class="analytics-metric-label">Reward done</span>
                <strong class="analytics-metric-value">{{ summary.rewardApprovedOrIssued }}</strong>
                <span class="analytics-metric-hint">{{ summary.rewardPendingVerification }} pending</span>
              </article>
              <article class="analytics-metric analytics-metric--reward">
                <span class="analytics-metric-label">Reward queue</span>
                <strong class="analytics-metric-value">{{ summary.rewardInProgress }}</strong>
                <span class="analytics-metric-hint">In progress / not started</span>
              </article>
              <article class="analytics-metric analytics-metric--traffic">
                <span class="analytics-metric-label">Site sessions (7d)</span>
                <strong class="analytics-metric-value">{{ siteSessions7dDisplay }}</strong>
                <span class="analytics-metric-hint">Page stays ≥ 1s</span>
              </article>
              <article class="analytics-metric analytics-metric--traffic">
                <span class="analytics-metric-label">Unique visitors (7d)</span>
                <strong class="analytics-metric-value">{{ uniqueVisitors7dDisplay }}</strong>
                <span class="analytics-metric-hint">Per browser session</span>
              </article>
              <article class="analytics-metric analytics-metric--traffic">
                <span class="analytics-metric-label">Time on site (7d)</span>
                <strong class="analytics-metric-value">{{ formatDurationShort(siteDwell7dDisplay) }}</strong>
                <span class="analytics-metric-hint">Total dwell tracked</span>
              </article>
            </div>
          </section>

          <section id="analytics-activity" class="analytics-section">
            <h3 class="analytics-section-head">
              Activity — 14 days
            </h3>
            <div class="analytics-chart-grid">
              <div class="analytics-panel analytics-panel--chart">
                <div class="analytics-panel-head">
                  <h4 class="analytics-panel-title">Reviews / day</h4>
                  <span class="analytics-panel-tag">UGC</span>
                </div>
                <div class="bar-chart bar-chart--analytics">
                  <div v-for="row in reviewsByDayRows" :key="'r' + row.day" class="bar-row">
                    <span class="bar-day">{{ shortDay(row.day) }}</span>
                    <div class="bar-track bar-track--analytics">
                      <div class="bar-fill fill-cyan" :style="{ width: barPct(row.count, maxReviews) }"></div>
                    </div>
                    <span class="bar-count">{{ row.count }}</span>
                  </div>
                </div>
              </div>
              <div class="analytics-panel analytics-panel--chart">
                <div class="analytics-panel-head">
                  <h4 class="analytics-panel-title">Comments / day</h4>
                  <span class="analytics-panel-tag analytics-panel-tag--soft">Threads</span>
                </div>
                <div class="bar-chart bar-chart--analytics">
                  <div v-for="row in commentsByDayRows" :key="'c' + row.day" class="bar-row">
                    <span class="bar-day">{{ shortDay(row.day) }}</span>
                    <div class="bar-track bar-track--analytics">
                      <div class="bar-fill fill-cyan-soft" :style="{ width: barPct(row.count, maxComments) }"></div>
                    </div>
                    <span class="bar-count">{{ row.count }}</span>
                  </div>
                </div>
              </div>
              <div class="analytics-panel analytics-panel--chart analytics-panel--span-2">
                <div class="analytics-panel-head">
                  <h4 class="analytics-panel-title">Site sessions / day</h4>
                  <span class="analytics-panel-tag analytics-panel-tag--violet">SPA</span>
                </div>
                <p class="analytics-panel-desc">Counted when users navigate or leave a route (dwell ≥ 1s).</p>
                <div class="bar-chart bar-chart--analytics bar-chart--compact">
                  <div v-for="row in siteSessionsChartRows" :key="'s' + row.day" class="bar-row">
                    <span class="bar-day">{{ shortDay(row.day) }}</span>
                    <div class="bar-track bar-track--analytics">
                      <div class="bar-fill fill-violet" :style="{ width: barPct(row.count, maxSiteSessionsChart) }"></div>
                    </div>
                    <span class="bar-count">{{ row.count }}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="analytics-audience" class="analytics-section">
            <h3 class="analytics-section-head">
              Audience & pages
            </h3>
            <div class="analytics-split">
              <div class="analytics-panel analytics-panel--tall">
                <div class="analytics-panel-head">
                  <h4 class="analytics-panel-title">Top pages by time on screen</h4>
                  <span class="analytics-panel-tag analytics-panel-tag--magenta">14d</span>
                </div>
                <p class="analytics-panel-desc">Ranked by total dwell; bar length = share of longest page.</p>
                <div v-if="!topPagesRows.length" class="analytics-empty">
                  No page dwell data yet — traffic tracking will fill this after real sessions.
                </div>
                <div v-else class="engagement-list engagement-list--analytics">
                  <div v-for="(row, idx) in topPagesRows" :key="'p' + idx" class="engagement-row">
                    <div class="engagement-meta">
                      <span class="engagement-title">{{ pageDisplayName(row) }}</span>
                      <span class="engagement-sub analytics-muted">{{ row.visitCount }} visits · avg {{ row.avgDwellSeconds }}s</span>
                    </div>
                    <div class="bar-track engagement-track bar-track--analytics">
                      <div
                        class="bar-fill fill-magenta"
                        :style="{ width: barPct(row.totalDwellSeconds, maxPageDwell) }"
                      ></div>
                    </div>
                    <span class="engagement-dwell">{{ formatDurationShort(row.totalDwellSeconds) }}</span>
                  </div>
                </div>
              </div>
              <div class="analytics-panel analytics-panel--tall">
                <div class="analytics-panel-head">
                  <h4 class="analytics-panel-title">Users by school</h4>
                  <span class="analytics-panel-tag analytics-panel-tag--amber">Profiles</span>
                </div>
                <p class="analytics-panel-desc">Non-admin accounts; empty school bucket shown as “Not set”.</p>
                <div v-if="!schoolRows.length" class="analytics-empty">No school breakdown returned.</div>
                <div v-else class="bar-chart school-chart bar-chart--analytics">
                  <div v-for="(row, idx) in schoolRows" :key="'sch' + idx" class="bar-row school-bar-row">
                    <span class="bar-day school-name" :title="row.school">{{ truncateSchool(row.school) }}</span>
                    <div class="bar-track bar-track--analytics">
                      <div class="bar-fill fill-amber" :style="{ width: barPct(row.userCount, maxSchoolUsers) }"></div>
                    </div>
                    <span class="bar-count">{{ row.userCount }}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="analytics-sessions" class="analytics-section">
            <h3 class="analytics-section-head">
              Recent sessions
            </h3>
            <div class="analytics-panel analytics-panel--table">
              <div class="analytics-panel-head">
                <h4 class="analytics-panel-title">Latest routed visits</h4>
              </div>
              <p class="analytics-panel-desc">Signed-in users show name/email; others show guest id.</p>
              <div class="table-wrap table-wrap--analytics">
                <table class="data-table data-table--analytics">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Visitor</th>
                      <th>Page</th>
                      <th>Dwell</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!recentSiteRows.length">
                      <td colspan="4" class="analytics-empty-cell">No session rows yet.</td>
                    </tr>
                    <template v-else>
                      <tr v-for="(r, idx) in recentSiteRows" :key="'sv' + idx">
                        <td class="mono">{{ formatTime(r.recordedAt) }}</td>
                        <td>
                          <span class="analytics-visitor">{{ r.visitorLabel }}</span>
                        </td>
                        <td>
                          <div class="analytics-page-name">{{ pageDisplayName(r) }}</div>
                          <div class="analytics-muted small path-ellipsis" :title="r.routePath">{{ r.routePath }}</div>
                        </td>
                        <td>{{ formatDurationShort(r.dwellSeconds) }}</td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section
            v-if="recentReviewsRows.length"
            id="analytics-reviews"
            class="analytics-section"
          >
            <h3 class="analytics-section-head">
              Latest reviews
            </h3>
            <div class="analytics-panel analytics-panel--table">
              <div class="table-panel-head analytics-table-toolbar">
                <h4 class="analytics-panel-title table-panel-title-inline">Newest in the database</h4>
                <div class="table-toolbar-actions">
                  <label class="sort-label" for="reviews-sort">Order</label>
                  <select id="reviews-sort" v-model="analyticsReviewsSort" class="queue-sort queue-sort--analytics">
                    <option value="recent">Most recent</option>
                    <option value="user">User (A–Z)</option>
                    <option value="listing">Listing (A–Z)</option>
                    <option value="id">Review ID (low → high)</option>
                  </select>
                </div>
              </div>
              <div class="table-wrap table-wrap--analytics">
                <table class="data-table data-table--analytics">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>User</th>
                      <th>Listing</th>
                      <th>ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="r in sortedRecentReviewsRows"
                      :key="r.reviewId"
                      class="analytics-review-row"
                      @click="openReviewInNewTab(r.reviewId, { houseId: r.houseId, houseLetId: r.houseLetId })"
                    >
                      <td class="mono">{{ formatTime(r.submittedAt) }}</td>
                      <td>
                        <div class="analytics-page-name">{{ r.userName || '—' }}</div>
                        <div class="analytics-muted small">{{ r.userEmail || '' }}</div>
                      </td>
                      <td>{{ r.houseName || '—' }}</td>
                      <td class="mono review-link-cell">#{{ r.reviewId }} ↗</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import { hasValidStoredToken } from '@/utils/auth';
const { parseBackendLocalDateTimeMs } = require('@/utils/parseBackendLocalDateTimeMs');

export default {
  name: 'RewardAdmin',
  data() {
    return {
      activeTab: 'rewards',
      submissions: [],
      selectedUserId: null,
      selectedDetail: null,
      loadingDetail: false,
      decisionNote: '',
      summary: null,
      analyticsLoading: false,
      analyticsError: null,
      detailRequestGen: 0,
      decisionSubmitting: false,
      reminderMessage: '',
      reminderSubmitting: false,
      queueSort: 'recent',
      analyticsReviewsSort: 'recent',
    };
  },
  computed: {
    sortedSubmissions() {
      const list = Array.isArray(this.submissions) ? [...this.submissions] : [];
      const mode = this.queueSort || 'recent';
      if (mode === 'name') {
        list.sort((a, b) => {
          const na = String(a.name || a.email || '').toLowerCase();
          const nb = String(b.name || b.email || '').toLowerCase();
          const cmp = na.localeCompare(nb, undefined, { sensitivity: 'base' });
          if (cmp !== 0) return cmp;
          return (a.userId || 0) - (b.userId || 0);
        });
        return list;
      }
      if (mode === 'email') {
        list.sort((a, b) => {
          const cmp = String(a.email || '')
            .toLowerCase()
            .localeCompare(String(b.email || '').toLowerCase(), undefined, { sensitivity: 'base' });
          if (cmp !== 0) return cmp;
          return (a.userId || 0) - (b.userId || 0);
        });
        return list;
      }
      if (mode === 'status') {
        list.sort((a, b) => {
          const da = this.statusRank(a.status);
          const db = this.statusRank(b.status);
          if (da !== db) return da - db;
          const tb = this.activityMillis(b.lastActivityAt);
          const ta = this.activityMillis(a.lastActivityAt);
          if (tb !== ta) return tb - ta;
          return (a.userId || 0) - (b.userId || 0);
        });
        return list;
      }
      list.sort((a, b) => {
        const tb = this.activityMillis(b.lastActivityAt);
        const ta = this.activityMillis(a.lastActivityAt);
        if (tb !== ta) return tb - ta;
        const rid = (Number(b.reviewId) || 0) - (Number(a.reviewId) || 0);
        if (rid !== 0) return rid;
        return (Number(b.userId) || 0) - (Number(a.userId) || 0);
      });
      return list;
    },
    sortedRecentReviewsRows() {
      const rows = this.recentReviewsRows;
      const list = Array.isArray(rows) ? [...rows] : [];
      const mode = this.analyticsReviewsSort || 'recent';
      if (mode === 'user') {
        list.sort((a, b) =>
          String(a.userName || a.userEmail || '')
            .toLowerCase()
            .localeCompare(String(b.userName || b.userEmail || '').toLowerCase(), undefined, {
              sensitivity: 'base',
            }),
        );
        return list;
      }
      if (mode === 'listing') {
        list.sort((a, b) =>
          String(a.houseName || '')
            .toLowerCase()
            .localeCompare(String(b.houseName || '').toLowerCase(), undefined, { sensitivity: 'base' }),
        );
        return list;
      }
      if (mode === 'id') {
        list.sort((a, b) => (Number(a.reviewId) || 0) - (Number(b.reviewId) || 0));
        return list;
      }
      list.sort((a, b) => {
        const d = this.activityMillis(b.submittedAt) - this.activityMillis(a.submittedAt);
        if (d !== 0) return d;
        return (Number(b.reviewId) || 0) - (Number(a.reviewId) || 0);
      });
      return list;
    },
    reviewsByDayRows() {
      const rows = this.summary && this.summary.reviewsByDay;
      return Array.isArray(rows) ? rows : [];
    },
    commentsByDayRows() {
      const rows = this.summary && this.summary.commentsByDay;
      return Array.isArray(rows) ? rows : [];
    },
    recentReviewsRows() {
      const rows = this.summary && this.summary.recentReviews;
      return Array.isArray(rows) ? rows : [];
    },
    maxReviews() {
      return this.maxSeries(this.reviewsByDayRows);
    },
    maxComments() {
      return this.maxSeries(this.commentsByDayRows);
    },
    topPagesRows() {
      const rows = this.summary && this.summary.topPagesByEngagement;
      return Array.isArray(rows) ? rows : [];
    },
    maxPageDwell() {
      const rows = this.topPagesRows;
      if (!rows.length) return 1;
      return Math.max(...rows.map((r) => Number(r.totalDwellSeconds) || 0), 1);
    },
    schoolRows() {
      const rows = this.summary && this.summary.usersBySchool;
      return Array.isArray(rows) ? rows : [];
    },
    maxSchoolUsers() {
      const rows = this.schoolRows;
      if (!rows.length) return 1;
      return Math.max(...rows.map((r) => Number(r.userCount) || 0), 1);
    },
    recentSiteRows() {
      const rows = this.summary && this.summary.recentSiteSessions;
      return Array.isArray(rows) ? rows : [];
    },
    siteSessions7dDisplay() {
      const v = this.summary && this.summary.siteSessionsLast7Days;
      return Number.isFinite(Number(v)) ? Number(v) : 0;
    },
    uniqueVisitors7dDisplay() {
      const v = this.summary && this.summary.siteUniqueVisitorsLast7Days;
      return Number.isFinite(Number(v)) ? Number(v) : 0;
    },
    siteDwell7dDisplay() {
      const v = this.summary && this.summary.siteTotalDwellSecondsLast7Days;
      return Number.isFinite(Number(v)) ? Number(v) : 0;
    },
    siteSessionsChartRows() {
      const rows = this.summary && this.summary.siteSessionsByDay;
      if (Array.isArray(rows) && rows.length > 0) {
        return rows;
      }
      const fallback = this.reviewsByDayRows;
      if (Array.isArray(fallback) && fallback.length > 0) {
        return fallback.map((r) => ({ day: r.day, count: 0 }));
      }
      return [];
    },
    maxSiteSessionsChart() {
      return this.maxSeries(this.siteSessionsChartRows);
    },
    hasLiveTraffic() {
      return this.siteSessions7dDisplay > 0 || this.uniqueVisitors7dDisplay > 0 || this.siteDwell7dDisplay > 0;
    },
    analyticsSectionNavItems() {
      const items = [
        { id: 'overview', label: 'Overview' },
        { id: 'activity', label: 'Activity (14 d)' },
        { id: 'audience', label: 'Audience & pages' },
        { id: 'sessions', label: 'Recent sessions' },
      ];
      if (this.recentReviewsRows.length > 0) {
        items.push({ id: 'reviews', label: 'Latest reviews' });
      }
      return items;
    },
    canSendReminder() {
      const s = this.selectedDetail && this.selectedDetail.summary;
      if (!s || !s.status) return false;
      const blocked = ['PENDING_VERIFICATION', 'APPROVED', 'REWARD_ISSUED'];
      return !blocked.includes(String(s.status));
    },
    suggestedReminderType() {
      const s = this.selectedDetail && this.selectedDetail.summary;
      if (!s) return 'IN_PROGRESS';
      return String(s.status) === 'NOT_STARTED' ? 'NOT_STARTED' : 'IN_PROGRESS';
    },
  },
  mounted() {
    if (!hasValidStoredToken()) {
      this.$router.replace({ path: '/LogReg', query: { redirect: this.$route.fullPath } });
      return;
    }
    this.loadQueue();
  },
  methods: {
    statusTone(label) {
      const s = String(label || '').toLowerCase();
      if (s.includes('approv') || s.includes('issued')) return 'tone-positive';
      if (s.includes('reject')) return 'tone-negative';
      if (s.includes('pending') || s.includes('verification')) return 'tone-warn';
      if (s.includes('progress')) return 'tone-info';
      return 'tone-neutral';
    },
    maxSeries(rows) {
      if (!rows || !rows.length) return 1;
      const m = Math.max(...rows.map((r) => Number(r.count) || 0), 1);
      return m;
    },
    barPct(count, max) {
      const c = Number(count) || 0;
      if (c === 0) return '0%';
      const pct = Math.round((c / (max || 1)) * 100);
      return `${Math.min(100, Math.max(4, pct))}%`;
    },
    shortDay(iso) {
      if (!iso) return '';
      const s = String(iso);
      const d = s.includes('T') ? new Date(s) : new Date(`${s}T12:00:00`);
      if (Number.isNaN(d.getTime())) return s;
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },
    formatTime(iso) {
      if (!iso) return '—';
      try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
      } catch (e) {
        return iso;
      }
    },
    formatDurationShort(rawSec) {
      let s = Number(rawSec);
      if (!Number.isFinite(s) || s < 0) return '0s';
      s = Math.round(s);
      if (s < 60) return `${s}s`;
      const m = Math.floor(s / 60);
      const rs = s % 60;
      if (m < 60) return `${m}m${rs ? ` ${rs}s` : ''}`;
      const h = Math.floor(m / 60);
      const rm = m % 60;
      return `${h}h${rm ? ` ${rm}m` : ''}`;
    },
    pageDisplayName(row) {
      if (!row) return '—';
      const n = row.routeName;
      const p = row.routePath;
      if (n != null && String(n).trim()) return String(n).trim();
      if (p != null && String(p).trim()) return String(p).trim();
      return '—';
    },
    truncateSchool(s) {
      const t = String(s || '');
      if (t.length <= 24) return t;
      return `${t.slice(0, 22)}…`;
    },
    activityMillis(val) {
      return parseBackendLocalDateTimeMs(val);
    },
    statusRank(status) {
      const s = String(status || '');
      const order = {
        PENDING_VERIFICATION: 0,
        REJECTED: 1,
        APPROVED: 2,
        IN_PROGRESS: 3,
        REWARD_ISSUED: 4,
        NOT_STARTED: 5,
      };
      return Object.prototype.hasOwnProperty.call(order, s) ? order[s] : 99;
    },
    switchAnalytics() {
      this.activeTab = 'analytics';
      if (!this.summary) this.loadAnalytics();
    },
    scrollAnalyticsSection(sectionId) {
      const el = document.getElementById(`analytics-${sectionId}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    parseResult(res, failureMessage) {
      const p = res && res.data;
      if (!p || Number(p.code) !== 1) {
        return { ok: false, msg: (p && p.msg) || failureMessage, data: null };
      }
      return { ok: true, msg: '', data: p.data };
    },
    loadAnalytics() {
      if (!hasValidStoredToken()) {
        this.analyticsError = 'Session expired. Please log in again.';
        return;
      }
      this.analyticsLoading = true;
      this.analyticsError = null;
      axios
        .get('/api/admin/analytics/summary')
        .then((res) => {
          const r = this.parseResult(res, 'Could not load analytics.');
          if (!r.ok) {
            this.analyticsError = r.msg;
            this.summary = null;
            return;
          }
          this.summary = r.data;
        })
        .catch((err) => {
          const st = err.response && err.response.status;
          const hint =
            st === 401
              ? 'Unauthorized — please sign in again (admin JWT required).'
              : '';
          this.analyticsError =
            hint ||
            (err.response && err.response.data && err.response.data.msg) ||
            err.message ||
            'Could not load analytics.';
        })
        .finally(() => {
          this.analyticsLoading = false;
        });
    },
    loadQueue() {
      if (!hasValidStoredToken()) {
        Swal.fire({
          icon: 'info',
          title: 'Session expired',
          text: 'Please log in again to use the admin console.',
          confirmButtonColor: '#3bc2ff',
        }).then(() =>
          this.$router.replace({ path: '/LogReg', query: { redirect: this.$route.fullPath } }),
        );
        return;
      }
      axios
        .get('/api/reward/admin/submissions')
        .then((response) => {
          const r = this.parseResult(response, 'Reward admin data could not be loaded.');
          if (!r.ok) {
            Swal.fire({
              icon: 'error',
              title: 'Unable to load queue',
              text: r.msg,
              confirmButtonColor: '#3bc2ff',
            });
            this.submissions = [];
            return;
          }
          this.submissions = Array.isArray(r.data) ? r.data : [];
          this.$nextTick(() => {
            const sorted = this.sortedSubmissions;
            if (!sorted.length) {
              this.selectedUserId = null;
              this.selectedDetail = null;
              this.decisionNote = '';
              return;
            }
            const prevId = this.selectedUserId;
            if (prevId && sorted.some((s) => s.userId === prevId)) {
              return;
            }
            this.selectSubmission(sorted[0].userId);
          });
        })
        .catch((error) => {
          this.submissions = [];
          this.selectedUserId = null;
          this.selectedDetail = null;
          this.decisionNote = '';
          Swal.fire({
            icon: 'error',
            title: 'Unable to load queue',
            text:
              error?.response?.status === 401
                ? 'Unauthorized — please sign in again.'
                : error?.response?.data?.msg || 'Reward admin data could not be loaded.',
            confirmButtonColor: '#3bc2ff',
          });
        });
    },
    safePhotoUrl(photo) {
      if (!photo || typeof photo !== 'string') return '';
      const t = photo.trim();
      return /^https?:\/\//i.test(t) ? t : '';
    },
    selectSubmission(userId) {
      this.selectedUserId = userId;
      this.detailRequestGen += 1;
      const reqGen = this.detailRequestGen;
      this.loadingDetail = true;
      axios
        .get(`/api/reward/admin/submissions/${userId}`)
        .then((response) => {
          if (reqGen !== this.detailRequestGen) return;
          const r = this.parseResult(response, 'Submission details could not be loaded.');
          if (!r.ok) {
            this.selectedDetail = null;
            Swal.fire({
              icon: 'error',
              title: 'Unable to load submission',
              text: r.msg,
              confirmButtonColor: '#3bc2ff',
            });
            return;
          }
          this.selectedDetail = r.data;
          this.decisionNote = this.selectedDetail?.summary?.adminNote || '';
          this.reminderMessage = '';
        })
        .catch((error) => {
          if (reqGen !== this.detailRequestGen) return;
          this.selectedDetail = null;
          Swal.fire({
            icon: 'error',
            title: 'Unable to load submission',
            text: error?.response?.data?.msg || 'Submission details could not be loaded.',
            confirmButtonColor: '#3bc2ff',
          });
        })
        .finally(() => {
          if (reqGen === this.detailRequestGen) this.loadingDetail = false;
        });
    },
    submitAction(action) {
      if (!this.selectedUserId || this.decisionSubmitting) return;

      if (!hasValidStoredToken()) {
        Swal.fire({
          icon: 'info',
          title: 'Session required',
          text: 'Your session expired or you are not signed in. Sign in again to approve or reject.',
          confirmButtonColor: '#3bc2ff',
        }).then(() =>
          this.$router.replace({ path: '/LogReg', query: { redirect: this.$route.fullPath } }),
        );
        return;
      }

      const post = () => {
        this.decisionSubmitting = true;
        axios
          .post(`/api/reward/admin/submissions/${this.selectedUserId}/${action}`, {
            note: this.decisionNote,
          })
          .then((response) => {
            const r = this.parseResult(response, 'The reward action could not be completed.');
            if (!r.ok) {
              Swal.fire({
                icon: 'error',
                title: 'Action failed',
                text: r.msg,
                confirmButtonColor: '#3bc2ff',
              });
              return;
            }
            this.selectedDetail = r.data || this.selectedDetail;
            this.loadQueue();

            if (action === 'approve') {
              const w =
                r.data && Array.isArray(r.data.eligibilityWarnings)
                  ? r.data.eligibilityWarnings.filter(Boolean)
                  : [];
              if (w.length > 0) {
                const esc = (s) =>
                  String(s)
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;');
                Swal.fire({
                  icon: 'warning',
                  title: 'Approved — missing usual requirements',
                  html:
                    '<p style="text-align:left;margin:0 0 0.75em">Student had not finished every campaign rule:</p>' +
                    '<ul style="text-align:left;margin:0;padding-left:1.25em">' +
                    w.map((x) => `<li>${esc(x)}</li>`).join('') +
                    '</ul>',
                  confirmButtonColor: '#3bc2ff',
                });
              }
            }
          })
          .catch((error) => {
            Swal.fire({
              icon: 'error',
              title: 'Action failed',
              text:
                error?.userMessage ||
                error?.message ||
                error?.response?.data?.msg ||
                'The reward action could not be completed.',
              confirmButtonColor: '#3bc2ff',
            });
          })
          .finally(() => {
            this.decisionSubmitting = false;
          });
      };

      if (action === 'reject') {
        Swal.fire({
          icon: 'warning',
          title: 'Reject this submission?',
          text: 'The user will be notified according to your workflow.',
          showCancelButton: true,
          confirmButtonText: 'Reject',
          cancelButtonText: 'Cancel',
          confirmButtonColor: '#ef4444',
        }).then((res) => {
          if (res.isConfirmed) post();
        });
        return;
      }

      post();
    },
    sendReminder(type) {
      if (!this.selectedUserId || this.reminderSubmitting || !this.canSendReminder) return;

      if (!hasValidStoredToken()) {
        Swal.fire({
          icon: 'info',
          title: 'Session required',
          text: 'Your session expired. Sign in again to send reminder emails.',
          confirmButtonColor: '#3bc2ff',
        }).then(() =>
          this.$router.replace({ path: '/LogReg', query: { redirect: this.$route.fullPath } }),
        );
        return;
      }

      const label = type === 'NOT_STARTED' ? 'not started' : 'incomplete';
      const name = this.selectedDetail?.summary?.name || this.selectedDetail?.summary?.email || 'this student';

      Swal.fire({
        icon: 'question',
        title: `Send ${label} reminder?`,
        text: `We'll email ${name} with their progress checklist and the HK$50 Starbucks reward offer.`,
        showCancelButton: true,
        confirmButtonText: 'Send email',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#3bc2ff',
      }).then((res) => {
        if (!res.isConfirmed) return;

        this.reminderSubmitting = true;
        axios
          .post(`/api/reward/admin/submissions/${this.selectedUserId}/remind`, {
            type,
            message: this.reminderMessage,
          })
          .then((response) => {
            const r = this.parseResult(response, 'The reminder email could not be sent.');
            if (!r.ok) {
              Swal.fire({
                icon: 'error',
                title: 'Email failed',
                text: r.msg,
                confirmButtonColor: '#3bc2ff',
              });
              return;
            }
            Swal.fire({
              icon: 'success',
              title: 'Reminder sent',
              text: 'The student should receive the email shortly.',
              confirmButtonColor: '#3bc2ff',
            });
          })
          .catch((error) => {
            Swal.fire({
              icon: 'error',
              title: 'Email failed',
              text:
                error?.response?.data?.msg ||
                error?.message ||
                'The reminder email could not be sent.',
              confirmButtonColor: '#3bc2ff',
            });
          })
          .finally(() => {
            this.reminderSubmitting = false;
          });
      });
    },
    openRouteInNewTab(routeLocation) {
      const resolved = this.$router.resolve(routeLocation);
      const href = resolved && resolved.href ? resolved.href : '#/';
      const url = /^https?:\/\//i.test(href)
        ? href
        : `${window.location.origin}${window.location.pathname}${href.startsWith('#') ? href : `#${href}`}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    async openReviewInNewTab(reviewId, hint = null) {
      const id = reviewId != null ? String(reviewId).trim() : '';
      if (!id || !/^\d+$/.test(id)) {
        Swal.fire({
          icon: 'info',
          title: 'Review unavailable',
          text: 'This row does not have a valid review ID.',
          confirmButtonColor: '#3bc2ff',
        });
        return;
      }

      let houseId = hint && hint.houseId != null ? String(hint.houseId) : '';
      let houseLetId = hint && hint.houseLetId != null ? String(hint.houseLetId) : '';

      if (!houseId || !houseLetId) {
        try {
          const res = await axios.get('/api/review/getReview', { params: { reviewId: id } });
          const r = res.data || {};
          if (r.houseId != null) houseId = String(r.houseId);
          if (r.houseLetId != null) houseLetId = String(r.houseLetId);
        } catch (err) {
          console.warn('Could not resolve listing for review', id, err);
        }
      }

      if (!houseId || !houseLetId) {
        Swal.fire({
          icon: 'error',
          title: 'Cannot open review',
          text: 'This review is not linked to a listing on Sparrow, so it cannot be opened the same way users browse reviews.',
          confirmButtonColor: '#3bc2ff',
        });
        return;
      }

      this.openRouteInNewTab({
        path: '/HouseReviewScore',
        query: { houseId, houseLetId },
      });
    },
    viewReviewPage() {
      const rev = this.selectedDetail && this.selectedDetail.review;
      this.openReviewInNewTab(rev && rev.reviewId, {
        houseId: rev && rev.houseId,
        houseLetId: rev && rev.houseLetId,
      });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap');

/* Page shell matches AddHouse.vue `.outer_box`; height clears App.vue 72px fixed navbar */
.admin-page {
  box-sizing: border-box;
  width: 100%;
  min-height: calc(100vh - 72px);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a;
  background: rgba(60, 194, 255, 1);
  padding: 0 0 48px;
}

.admin-top-bar {
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.admin-top-inner {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
}

.admin-top-brand {
  flex: 1;
  min-width: 220px;
}

.admin-hub-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #3bc2ff;
  margin-bottom: 8px;
}

.admin-hub-title {
  margin: 0;
  font-size: clamp(1.5rem, 3.5vw, 2rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #0f172a;
}

.admin-hub-lead {
  margin: 8px 0 0;
  max-width: 480px;
  color: #64748b;
  line-height: 1.55;
  font-size: 0.92rem;
}

/* Tab strip ~ filter pills on browse pages */
.admin-hub-tabs {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  border-radius: 50px;
  border: 2px solid #e2e8f0;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.admin-hub-tab {
  border: none;
  background: transparent;
  color: #64748b;
  padding: 10px 22px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s, box-shadow 0.2s, transform 0.15s;
}

.admin-hub-tab:hover {
  color: #0f172a;
}

.admin-hub-tab.active {
  color: #0f172a;
  background: #ffffff;
  box-shadow: 0 0 0 2px #3bc2ff, 0 8px 24px rgba(59, 194, 255, 0.15);
  transform: translateY(-1px);
}

.admin-hub-body {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px 24px 56px;
}

.analytics-body {
  padding-top: 8px;
}

/* ——— Analytics (aligned with reward admin panels) ——— */
.analytics-shell {
  max-width: 1280px;
  margin: 0 auto;
}

.analytics-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  margin-bottom: 20px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.analytics-toolbar-copy {
  min-width: 0;
}

.analytics-toolbar-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.02em;
}

.analytics-toolbar-sub {
  margin: 6px 0 0;
  font-size: 0.88rem;
  line-height: 1.45;
  color: #64748b;
}

.analytics-refresh {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font: inherit;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  color: #0f172a;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.analytics-refresh:hover:not(:disabled) {
  border-color: #3bc2ff;
  box-shadow: 0 0 0 2px rgba(59, 194, 255, 0.2);
}

.analytics-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.analytics-banner {
  margin-bottom: 20px;
  border-radius: 16px;
}

.analytics-callout {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  margin-bottom: 20px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #334155;
  font-size: 0.875rem;
  line-height: 1.55;
}

.analytics-callout strong {
  font-size: 0.9375rem;
  color: #0f172a;
}

.analytics-code {
  padding: 1px 7px;
  border-radius: 6px;
  background: #f1f5f9;
  font-family: ui-monospace, monospace;
  font-size: 0.82em;
}

.analytics-section-nav {
  position: sticky;
  top: 0;
  z-index: 4;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  margin: 0 0 24px;
  border-radius: 12px;
  background: rgba(248, 250, 252, 0.92);
  backdrop-filter: blur(8px);
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.analytics-section-nav-btn {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  font: inherit;
  font-weight: 600;
  font-size: 0.8125rem;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.analytics-section-nav-btn:hover {
  color: #0f172a;
  border-color: #3bc2ff;
}

.analytics-section {
  margin-bottom: 32px;
  scroll-margin-top: 92px;
}

.analytics-section-head {
  margin: 0 0 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e2e8f0;
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
}

.analytics-grid--metrics {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 16px;
}

.analytics-metric {
  position: relative;
  padding: 16px 16px 14px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.analytics-metric::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  border-radius: 14px 0 0 14px;
  background: var(--metric-accent, #3bc2ff);
}

.analytics-metric:hover {
  border-color: #cbd5e1;
}

.analytics-metric--sky {
  --metric-accent: linear-gradient(180deg, #3bc2ff, #0284c7);
}

.analytics-metric--sky::before {
  background: linear-gradient(180deg, #3bc2ff, #0284c7);
}

.analytics-metric--reward {
  --metric-accent: #a855f7;
}

.analytics-metric--reward::before {
  background: linear-gradient(180deg, #c084fc, #7c3aed);
}

.analytics-metric--traffic {
  --metric-accent: #6366f1;
}

.analytics-metric--traffic::before {
  background: linear-gradient(180deg, #818cf8, #4f46e5);
}

.analytics-metric-label {
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #64748b;
}

.analytics-metric-value {
  display: block;
  margin-top: 8px;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #0f172a;
  line-height: 1.1;
}

.analytics-metric-hint {
  display: block;
  margin-top: 8px;
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.35;
}

.analytics-chart-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}

@media (max-width: 900px) {
  .analytics-chart-grid {
    grid-template-columns: 1fr;
  }
}

.analytics-panel {
  border-radius: 16px;
  padding: 20px 22px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.analytics-panel--span-2 {
  grid-column: 1 / -1;
}

.analytics-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
}

.analytics-panel-title {
  margin: 0;
  font-size: 0.975rem;
  font-weight: 700;
  color: #1e293b;
}

.analytics-panel-tag {
  flex-shrink: 0;
  padding: 4px 11px;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #0369a1;
  background: rgba(59, 194, 255, 0.18);
  border: 1px solid rgba(59, 194, 255, 0.35);
}

.analytics-panel-tag--soft {
  color: #0e7490;
  background: rgba(34, 211, 238, 0.15);
  border-color: rgba(34, 211, 238, 0.35);
}

.analytics-panel-tag--violet {
  color: #5b21b6;
  background: rgba(139, 92, 246, 0.16);
  border-color: rgba(124, 58, 237, 0.35);
}

.analytics-panel-tag--magenta {
  color: #9d174d;
  background: rgba(236, 72, 153, 0.14);
  border-color: rgba(219, 39, 119, 0.35);
}

.analytics-panel-tag--amber {
  color: #9a3412;
  background: rgba(251, 191, 36, 0.2);
  border-color: rgba(245, 158, 11, 0.45);
}

.analytics-panel-desc {
  margin: 0 0 16px;
  font-size: 0.84rem;
  color: #64748b;
  line-height: 1.5;
}

.analytics-split {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
  align-items: stretch;
}

@media (max-width: 960px) {
  .analytics-split {
    grid-template-columns: 1fr;
  }
}

.analytics-panel--tall {
  min-height: 120px;
}

.analytics-empty {
  padding: 28px 16px;
  text-align: center;
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.55;
  background: rgba(241, 245, 249, 0.65);
  border-radius: 14px;
  border: 1px dashed #cbd5e1;
}

.bar-chart--analytics {
  gap: 7px;
}

.bar-chart--compact .bar-row {
  font-size: 0.74rem;
}

.bar-track--analytics {
  height: 11px;
  background: rgba(241, 245, 249, 0.95);
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.06);
}

.engagement-list--analytics .engagement-row {
  padding: 10px 12px;
  margin: 0 -4px;
  border-radius: 12px;
  transition: background 0.15s ease;
}

.engagement-list--analytics .engagement-row:hover {
  background: rgba(59, 194, 255, 0.06);
}

.analytics-muted {
  color: #64748b !important;
}

.table-wrap--analytics {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.data-table--analytics {
  font-size: 0.86rem;
}

.data-table--analytics th {
  background: #f8fafc;
  color: #475569;
  border-bottom-color: #e2e8f0;
  padding: 12px 14px;
}

.data-table--analytics td {
  padding: 12px 14px;
  border-bottom-color: #f1f5f9;
}

.data-table--analytics tbody tr:hover td {
  background: rgba(59, 194, 255, 0.06);
}

.analytics-review-row {
  cursor: pointer;
}

.analytics-review-row:hover td {
  background: rgba(59, 194, 255, 0.1);
}

.review-link-cell {
  color: #0284c7;
  font-weight: 700;
}

.analytics-empty-cell {
  text-align: center;
  color: #64748b;
  font-style: italic;
  padding: 28px 16px !important;
}

.analytics-page-name {
  font-weight: 600;
  color: #0f172a;
}

.analytics-visitor {
  font-weight: 600;
  color: #334155;
}

.queue-sort--analytics {
  min-width: 160px;
  border-radius: 12px;
}

.analytics-table-toolbar {
  margin-bottom: 8px;
}

.table-panel-title-inline {
  margin: 0;
  font-size: 0.975rem;
  font-weight: 700;
}

@media (max-width: 640px) {
  .analytics-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .analytics-refresh {
    width: 100%;
  }

  .analytics-section {
    scroll-margin-top: 120px;
  }
}

.reward-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}
.reward-toolbar-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.sort-label {
  font-size: 13px;
  font-weight: 600;
  color: rgba(15, 23, 42, 0.75);
}
.queue-sort {
  font: inherit;
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid rgba(15, 23, 42, 0.15);
  background: #fff;
  color: #0f172a;
  min-width: 200px;
}
.table-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}
.table-panel-head .panel-title {
  margin: 0;
}
.table-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.reward-toolbar-hint {
  margin: 0;
  font-size: 0.92rem;
  color: #475569;
  font-weight: 500;
}

/* recommendHouse button primitives */
.primary-btn {
  background: #3bc2ff;
  color: white;
  border: none;
  padding: 12px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s;
}
.primary-btn:hover:not(:disabled) {
  background: #0284c7;
  transform: translateY(-2px);
}
.primary-btn.sm {
  padding: 10px 24px;
  font-size: 0.9rem;
  font-weight: 700;
  border-radius: 10px;
  min-width: 120px;
  box-shadow: 0 4px 12px rgba(59, 194, 255, 0.3);
}
.primary-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}

.text-btn {
  background: transparent;
  border: 2px solid #e2e8f0;
  color: #64748b;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 10px;
  transition: 0.3s;
  min-width: 80px;
}
.text-btn:hover:not(:disabled) {
  border-color: #3bc2ff;
  color: #3bc2ff;
  background: rgba(59, 194, 255, 0.05);
  transform: translateY(-1px);
}
.text-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.outline-btn {
  background: transparent;
  border: 1px solid #e2e8f0;
  color: #64748b;
  padding: 12px 20px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: 0.3s;
}
.outline-btn:hover {
  border-color: #94a3b8;
  color: #0f172a;
}

.outline-btn.btn-danger {
  border: 2px solid #fecaca;
  color: #b91c1c;
}
.outline-btn.btn-danger:hover {
  border-color: #ef4444;
  background: #fef2f2;
  color: #991b1b;
}

.reward-split {
  display: grid;
  grid-template-columns: minmax(280px, 340px) 1fr;
  gap: 20px;
  align-items: start;
}

.reward-rail {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: calc(100vh - 220px);
  overflow: auto;
  padding-right: 4px;
}

.reward-rail-empty {
  background: rgba(255, 255, 255, 0.85);
  border-radius: 16px;
  padding: 20px;
  color: #64748b;
  border: 1px dashed #cbd5e1;
}

.reward-rail-card {
  text-align: left;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px 16px;
  background: #ffffff;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.reward-rail-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
}

.reward-rail-card.active {
  border-color: #3bc2ff;
  box-shadow: 0 0 0 2px rgba(59, 194, 255, 0.35);
}

.reward-rail-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.reward-rail-name {
  font-weight: 700;
  color: #0f172a;
  font-size: 0.95rem;
}

.reward-rail-email {
  font-size: 0.85rem;
  color: #64748b;
  margin-top: 4px;
}

.reward-rail-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
  font-size: 0.78rem;
  color: #475569;
}

.reward-rail-metrics em {
  font-style: normal;
  font-weight: 700;
  color: #94a3b8;
  margin-right: 4px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

.status-pill.lg {
  font-size: 0.82rem;
  padding: 6px 14px;
}

.tone-positive {
  background: #d1fae5;
  color: #065f46;
}
.tone-negative {
  background: #fee2e2;
  color: #991b1b;
}
.tone-warn {
  background: #fef3c7;
  color: #92400e;
}
.tone-info {
  background: #e0f2fe;
  color: #0369a1;
}
.tone-neutral {
  background: #e2e8f0;
  color: #334155;
}

.reward-main {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.glass-panel {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  padding: 22px 24px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.hero-detail h2 {
  margin: 0;
  font-size: 1.35rem;
  color: #0f172a;
}

.muted {
  color: #64748b;
}

.small {
  font-size: 0.8rem;
}

.mono {
  font-family: ui-monospace, monospace;
  font-size: 0.82rem;
}

.hero-detail-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.stat-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 18px;
}

.stat-chip {
  flex: 1;
  min-width: 120px;
  background: #f8fafc;
  border-radius: 14px;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-left: 3px solid #3bc2ff;
}

.stat-chip-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin-bottom: 4px;
}

.stat-chip strong {
  font-size: 1.15rem;
  color: #0f172a;
}

.alert-banner {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 0.9rem;
}

.alert-banner.warn {
  background: #fffbeb;
  border: 1px solid #fcd34d;
  color: #78350f;
}

.alert-banner.flags {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

.alert-banner.flags strong {
  color: #b91c1c;
}

.alert-banner.note {
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.panel-title {
  margin: 0 0 12px;
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
}

.review-target {
  font-weight: 600;
  color: #0284c7;
  margin: 0 0 8px;
}

.review-body {
  margin: 0;
  line-height: 1.65;
  color: #334155;
}

.photo-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.photo-chip-slot {
  display: contents;
}

.photo-chip {
  display: inline-block;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(59, 194, 255, 0.12);
  color: #0369a1;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
}

.photo-chip:hover {
  background: rgba(59, 194, 255, 0.22);
}

.photo-chip-bad-url {
  cursor: not-allowed;
  opacity: 0.75;
  color: #64748b;
  background: #f1f5f9;
}

.decision-submit-hint {
  margin: 10px 0 0;
}

.comment-stack {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.comment-card {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.comment-card-title {
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}

.comment-card p {
  margin: 0;
  color: #475569;
  line-height: 1.5;
}

.flag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.flag {
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  font-weight: 600;
}

.decision-input {
  width: 100%;
  min-height: 100px;
  border: 1px solid #dbe4ef;
  border-radius: 12px;
  padding: 14px;
  font: inherit;
  resize: vertical;
  background: #fff;
  color: #0f172a;
}

.decision-input:focus {
  outline: none;
  border-color: #3bc2ff;
  box-shadow: 0 0 0 3px rgba(59, 194, 255, 0.2);
}

.decision-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 14px;
}

.decision-actions .primary-btn.sm,
.decision-actions .outline-btn {
  flex: 0 1 auto;
}

.review-actions {
  display: flex;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.review-actions .primary-btn.sm {
  flex: 0 1 auto;
}

.reminder-lead {
  margin: 0 0 14px;
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.55;
}

.reminder-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.reminder-preview-item {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fcd34d;
}

.reminder-preview-item.met {
  background: #d1fae5;
  color: #065f46;
  border-color: #6ee7b7;
}

.reminder-input {
  min-height: 80px;
}

.reminder-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 14px;
}

.reminder-hint {
  margin: 10px 0 0;
  font-size: 0.85rem;
}

.panel-muted {
  background: #ffffff;
  border-radius: 16px;
  padding: 40px 24px;
  text-align: center;
  color: #64748b;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.soft-pad {
  padding: 8px 0;
}

.analytics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}

.metric-tile {
  background: #ffffff;
  border-radius: 16px;
  padding: 18px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.metric-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.metric-value {
  display: block;
  font-size: 1.75rem;
  margin-top: 8px;
  color: #0f172a;
  letter-spacing: -0.02em;
}

.metric-sub {
  display: block;
  margin-top: 6px;
  font-size: 0.82rem;
  color: #64748b;
}

.charts-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.chart-panel {
  min-height: 200px;
}

.bar-chart {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bar-row {
  display: grid;
  grid-template-columns: 52px 1fr 28px;
  align-items: center;
  gap: 10px;
  font-size: 0.8rem;
}

.bar-day {
  color: #64748b;
  font-weight: 600;
}

.bar-track {
  height: 10px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: 999px;
  min-width: 4px;
  transition: width 0.4s ease;
}

.fill-cyan {
  background: linear-gradient(90deg, #3bc2ff, #0284c7);
}
.fill-cyan-soft {
  background: linear-gradient(90deg, rgba(59, 194, 255, 0.85), #67e8f9);
}

.fill-violet {
  background: linear-gradient(90deg, #8b5cf6, #6366f1);
}

.fill-magenta {
  background: linear-gradient(90deg, #ec4899, #a855f7);
}

.fill-amber {
  background: linear-gradient(90deg, #f59e0b, #f97316);
}

.metric-tile-accent {
  border-color: rgba(139, 92, 246, 0.35);
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.08);
}

.panel-hint {
  margin: 0 0 14px;
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.45;
}

.table-hint {
  margin-bottom: 12px;
}

.chart-panel-wide {
  grid-column: 1 / -1;
}

.analytics-split-row {
  align-items: start;
}

.engagement-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.engagement-row {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 10px 14px;
  align-items: center;
  font-size: 0.85rem;
}

@media (max-width: 720px) {
  .engagement-row {
    grid-template-columns: 1fr;
  }
  .engagement-dwell {
    justify-self: start;
  }
}

.engagement-meta {
  min-width: 0;
}

.engagement-title {
  display: block;
  font-weight: 700;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.engagement-sub {
  display: block;
  margin-top: 2px;
}

.engagement-track {
  min-width: 48px;
}

.engagement-dwell {
  font-weight: 700;
  color: #475569;
  font-size: 0.8rem;
  white-space: nowrap;
}

.school-chart .school-bar-row {
  grid-template-columns: 100px 1fr 36px;
}

.school-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100px;
}

.path-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
}

.bar-count {
  text-align: right;
  font-weight: 700;
  color: #475569;
}

.table-wrap {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}

.data-table th {
  text-align: left;
  padding: 10px 12px;
  border-bottom: 2px solid #e2e8f0;
  color: #64748b;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.06em;
}

.data-table td {
  padding: 12px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
}

@media (max-width: 900px) {
  .admin-page {
    padding: 0 0 40px;
  }
  .admin-top-inner {
    flex-direction: column;
    align-items: stretch;
  }
  .reward-split {
    grid-template-columns: 1fr;
  }
  .reward-rail {
    max-height: none;
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 8px;
  }
  .reward-rail-card {
    min-width: 260px;
  }
}
</style>
